import * as yup from 'yup';
import { ValidationError } from 'yup';
import svStrings from '../../../sv-strings';
import { QuizReward, QuizRewardComparison, QuizRewardType } from './types';

const MESSAGE_MAX_LENGTH = 500;
const VOUCHER_CODE_MAX_LENGTH = 50;

/** The reward card's form state. The threshold is kept as typed, so it can be empty. */
export type QuizRewardForm = {
  enabled: boolean;
  comparison: QuizRewardComparison;
  threshold: string;
  type: QuizRewardType;
  message: string;
  voucherCode: string;
};

export type QuizRewardErrors = Partial<Record<'threshold' | 'message' | 'voucherCode', string>>;

export const quizRewardSchema = yup.object({
  comparison: yup
    .string()
    .oneOf(['atLeast', 'moreThan'])
    .required(svStrings.validations.isRequired),
  threshold: yup
    .number()
    .typeError(svStrings.validations.mustBeInteger)
    .integer(svStrings.validations.mustBeInteger)
    .when('comparison', {
      is: 'moreThan',
      then: (schema) => schema.min(0, svStrings.validations.validQuizRewardThresholdMoreThan),
      otherwise: (schema) => schema.min(1, svStrings.validations.validQuizRewardThresholdAtLeast),
    }),
  type: yup.string().oneOf(['text', 'voucher']).required(svStrings.validations.isRequired),
  message: yup
    .string()
    .trim()
    .required(svStrings.validations.isRequired)
    .max(MESSAGE_MAX_LENGTH, svStrings.validations.validQuizRewardMessageMaxLength),
  voucherCode: yup
    .string()
    .trim()
    .when('type', {
      is: 'voucher',
      then: (schema) =>
        schema
          .required(svStrings.validations.isRequired)
          .max(VOUCHER_CODE_MAX_LENGTH, svStrings.validations.validQuizRewardVoucherCodeMaxLength),
    }),
});

/** Turns a stored reward into the card's form state; no reward gives a switched-off form. */
export const toQuizRewardForm = (reward?: QuizReward | null): QuizRewardForm => ({
  enabled: !!reward,
  comparison: reward?.comparison ?? 'atLeast',
  threshold: reward ? String(reward.threshold) : '',
  type: reward?.type ?? 'text',
  message: reward?.message ?? '',
  voucherCode: reward?.voucherCode ?? '',
});

/**
 * Validates the form, returning an error message per invalid field. A switched-off form has
 * no errors. Does not know the number of questions; see `getQuizRewardThresholdError`.
 */
export const getQuizRewardErrors = (form: QuizRewardForm): QuizRewardErrors => {
  if (!form.enabled) return {};

  try {
    quizRewardSchema.validateSync(form, { abortEarly: false });
    return {};
  } catch (error) {
    if (!(error instanceof ValidationError)) throw error;

    return error.inner.reduce<QuizRewardErrors>((errors, { path, message }) => {
      const key = path as keyof QuizRewardErrors;
      return errors[key] ? errors : { ...errors, [key]: message };
    }, {});
  }
};

/**
 * The reward to save: `null` when switched off, otherwise the form with the threshold as a
 * number and the message and voucher code trimmed. Text rewards are saved without a code.
 */
export const toQuizReward = (form: QuizRewardForm): QuizReward | null => {
  if (!form.enabled) return null;

  const reward: QuizReward = {
    comparison: form.comparison,
    threshold: Number(form.threshold),
    type: form.type,
    message: form.message.trim(),
  };

  return form.type === 'voucher' ? { ...reward, voucherCode: form.voucherCode.trim() } : reward;
};

/** The number of right answers a reward needs: "fler än N" needs N + 1. */
export const getMinCorrectAnswers = ({ comparison, threshold }: QuizReward): number =>
  comparison === 'moreThan' ? threshold + 1 : threshold;

/**
 * Checks a reward against the quiz's number of questions, which is only known once the
 * questions are added. Returns an error message when the reward needs more right answers
 * than there are questions, otherwise `null`.
 */
export const getQuizRewardThresholdError = (
  reward: QuizReward | null | undefined,
  questionCount: number
): string | null => {
  if (!reward || getMinCorrectAnswers(reward) <= questionCount) return null;

  return svStrings.polls.quizRewardThresholdTooHigh.replace('$count', String(questionCount));
};

/** A one-line summary of a reward, e.g. "Belöning: minst 7 av 10 rätt · rabattkod". */
export const getQuizRewardSummary = (reward: QuizReward, questionCount: number): string =>
  svStrings.polls.quizRewardSummary
    .replace(
      '$comparison',
      reward.comparison === 'moreThan'
        ? svStrings.polls.quizRewardSummaryMoreThan
        : svStrings.polls.quizRewardSummaryAtLeast
    )
    .replace('$threshold', String(reward.threshold))
    .replace('$count', String(questionCount))
    .replace(
      '$type',
      reward.type === 'voucher'
        ? svStrings.polls.quizRewardSummaryVoucher
        : svStrings.polls.quizRewardSummaryText
    );
